/**
 * Draggable dividers over the grid.
 *
 * The handles sit on top of the gap between panes rather than between them as
 * grid children, so the layout stays a plain two-by-two and the cells need to
 * know nothing about resizing.
 *
 * Dragging updates a local ratio every pointer move and only writes to the
 * vault on release — each save is an encrypted PATCH, and a drag produces
 * hundreds of moves.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { clampSplit } from '../types';

interface Props {
  /** Which handles this layout has. */
  vertical: boolean;
  horizontal: boolean;
  split: { x: number; y: number };
  /** Live position during a drag; the grid follows this. */
  onPreview(split: { x: number; y: number }): void;
  /** Final position, once the pointer is released. */
  onCommit(split: { x: number; y: number }): void;
  /** The element the ratios are measured against. */
  gridRef: React.RefObject<HTMLDivElement | null>;
}

export function Splitter({ vertical, horizontal, split, onPreview, onCommit, gridRef }: Props) {
  const [dragging, setDragging] = useState<'x' | 'y' | null>(null);
  const latest = useRef(split);
  latest.current = split;

  const start = useCallback(
    (axis: 'x' | 'y') => (event: React.PointerEvent) => {
      event.preventDefault();
      event.stopPropagation(); // do not also select the cell underneath
      setDragging(axis);
    },
    [],
  );

  useEffect(() => {
    if (!dragging) return;

    const move = (event: PointerEvent) => {
      const box = gridRef.current?.getBoundingClientRect();
      if (!box) return;
      const ratio =
        dragging === 'x'
          ? (event.clientX - box.left) / box.width
          : (event.clientY - box.top) / box.height;
      const next = { ...latest.current, [dragging]: clampSplit(ratio) };
      latest.current = next;
      onPreview(next);
    };

    const end = () => {
      setDragging(null);
      onCommit(latest.current);
    };

    // On window, not the handle: the pointer routinely outruns a 6px target.
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
    };
  }, [dragging, gridRef, onCommit, onPreview]);

  /** Keyboard nudging, so the split is not mouse-only. */
  const nudge = (axis: 'x' | 'y', delta: number) => {
    const next = { ...latest.current, [axis]: clampSplit(latest.current[axis] + delta) };
    latest.current = next;
    onPreview(next);
    onCommit(next);
  };

  return (
    <>
      {vertical && (
        <div
          className={`splitter splitter--v${dragging === 'x' ? ' splitter--active' : ''}`}
          style={{ left: `${split.x * 100}%` }}
          onPointerDown={start('x')}
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={Math.round(split.x * 100)}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') nudge('x', -0.02);
            if (e.key === 'ArrowRight') nudge('x', 0.02);
          }}
          title="Drag to resize — double-click to even up"
          onDoubleClick={() => nudge('x', 0.5 - latest.current.x)}
        />
      )}
      {horizontal && (
        <div
          className={`splitter splitter--h${dragging === 'y' ? ' splitter--active' : ''}`}
          style={{ top: `${split.y * 100}%` }}
          onPointerDown={start('y')}
          role="separator"
          aria-orientation="horizontal"
          aria-valuenow={Math.round(split.y * 100)}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp') nudge('y', -0.02);
            if (e.key === 'ArrowDown') nudge('y', 0.02);
          }}
          title="Drag to resize — double-click to even up"
          onDoubleClick={() => nudge('y', 0.5 - latest.current.y)}
        />
      )}
    </>
  );
}
